import {
  dueGroupSchema,
  myTasksBoardColumn,
  myTasksBoardColumnSchema,
  sortDirectionSchema,
  statusCategorySchema,
  taskPrioritySchema,
  type DueGroup,
  type MyTask,
  type MyTasksBoardColumn,
  type SortDirection,
} from '@pm-tool/shared';
import { z } from 'zod';

/**
 * Filters, sorting and grouping of the page "Meine Tasks" (DASH-01). The page holds all open
 * tasks of the person (at most MY_TASKS_LIMIT), so this runs in the browser; the state lives in
 * the URL like the task list of a project (TASK-04).
 */
export const myTasksSortSchema = z.enum(['key', 'title', 'project', 'priority', 'status', 'due']);
export type MyTasksSort = z.infer<typeof myTasksSortSchema>;

export const myTasksGroupSchema = z.enum(['due', 'project', 'none']);
export type MyTasksGroup = z.infer<typeof myTasksGroupSchema>;

/** List (default) or board by due date (E-39). */
export const myTasksViewSchema = z.enum(['list', 'board']);
export type MyTasksView = z.infer<typeof myTasksViewSchema>;

/** Only open tasks are listed, so "done" is no filter option. */
export const openStatusCategorySchema = statusCategorySchema.exclude(['done']);

const fieldSchemas = {
  q: z.string().trim().min(1).max(200),
  project: z.array(z.uuid()).min(1),
  priority: z.array(taskPrioritySchema).min(1),
  status: z.array(openStatusCategorySchema).min(1),
  due: z.array(dueGroupSchema).min(1),
  sort: myTasksSortSchema,
  dir: sortDirectionSchema,
  group: myTasksGroupSchema,
  view: myTasksViewSchema,
};

export type MyTasksSearch = {
  [K in keyof typeof fieldSchemas]?: z.infer<(typeof fieldSchemas)[K]>;
};

/** Invalid values are dropped one by one, so an old or edited link still opens the page. */
export function parseMyTasksSearch(raw: Record<string, unknown>): MyTasksSearch {
  const result: Record<string, unknown> = {};
  for (const [key, schema] of Object.entries(fieldSchemas)) {
    const value = raw[key];
    if (value === undefined || value === '') continue;
    const parsed = (schema as z.ZodType).safeParse(value); // each entry is a Zod schema
    if (parsed.success) result[key] = parsed.data;
  }
  return result;
}

/** Applies a change; `undefined` removes the value (filter cleared, default grouping). */
export function patchSearch(
  search: MyTasksSearch,
  patch: { [K in keyof MyTasksSearch]?: MyTasksSearch[K] | undefined },
): MyTasksSearch {
  const merged = Object.entries({ ...search, ...patch }).filter(([, v]) => v !== undefined);
  return Object.fromEntries(merged);
}

export const DEFAULT_SORT: MyTasksSort = 'due';

/** Direction when a column is sorted first: most urgent / most important on top. */
export function defaultDirection(sort: MyTasksSort): SortDirection {
  return sort === 'priority' ? 'desc' : 'asc';
}

/** Next sort state after a click on a column header: toggle the direction or switch columns. */
export function toggleSort(search: MyTasksSearch, field: MyTasksSort): MyTasksSearch {
  const current = search.sort ?? DEFAULT_SORT;
  const dir = search.dir ?? defaultDirection(current);
  const next: MyTasksSearch = { ...search, sort: field };
  next.dir = current === field ? (dir === 'asc' ? 'desc' : 'asc') : defaultDirection(field);
  return next;
}

export function hasMyTasksFilters(search: MyTasksSearch): boolean {
  return (
    search.q !== undefined ||
    search.project !== undefined ||
    search.priority !== undefined ||
    search.status !== undefined ||
    search.due !== undefined
  );
}

export function withoutFilters(search: MyTasksSearch): MyTasksSearch {
  const next = { ...search };
  delete next.q;
  delete next.project;
  delete next.priority;
  delete next.status;
  delete next.due;
  return next;
}

export function filterMyTasks(items: readonly MyTask[], search: MyTasksSearch): MyTask[] {
  const q = search.q?.toLocaleLowerCase('de-DE');
  return items.filter(
    (t) =>
      (q === undefined ||
        t.key.toLocaleLowerCase('de-DE').includes(q) ||
        t.title.toLocaleLowerCase('de-DE').includes(q)) &&
      (search.project === undefined || search.project.includes(t.project.id)) &&
      (search.priority === undefined || search.priority.includes(t.priority)) &&
      (search.status === undefined || search.status.some((c) => c === t.status.category)) &&
      (search.due === undefined || search.due.includes(t.group)),
  );
}

const collator = new Intl.Collator('de-DE', { numeric: true, sensitivity: 'base' });
const priorityRank = (t: MyTask) => taskPrioritySchema.options.indexOf(t.priority);
const categoryRank = (t: MyTask) => statusCategorySchema.options.indexOf(t.status.category);

/** Tasks without a due date come last in both directions. */
function compareDue(a: MyTask, b: MyTask, dir: SortDirection): number {
  if (a.dueDate === b.dueDate) return 0;
  if (a.dueDate === null) return 1;
  if (b.dueDate === null) return -1;
  const cmp = a.dueDate < b.dueDate ? -1 : 1;
  return dir === 'asc' ? cmp : -cmp;
}

const compareBy: Record<Exclude<MyTasksSort, 'due'>, (a: MyTask, b: MyTask) => number> = {
  key: (a, b) => collator.compare(a.key, b.key),
  title: (a, b) => collator.compare(a.title, b.title),
  project: (a, b) => collator.compare(a.project.name, b.project.name),
  priority: (a, b) => priorityRank(a) - priorityRank(b),
  status: (a, b) =>
    categoryRank(a) - categoryRank(b) || collator.compare(a.status.name, b.status.name),
};

/** Sorted copy; ties fall back to due date (ascending), then priority, then key. */
export function sortMyTasks(items: readonly MyTask[], search: MyTasksSearch): MyTask[] {
  const sort = search.sort ?? DEFAULT_SORT;
  const dir = search.dir ?? defaultDirection(sort);
  const sign = dir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    const primary = sort === 'due' ? compareDue(a, b, dir) : sign * compareBy[sort](a, b);
    return (
      primary || compareDue(a, b, 'asc') || priorityRank(b) - priorityRank(a) || compareBy.key(a, b)
    );
  });
}

export type MyTasksSection = {
  id: string;
  /** Due group or project name; `undefined` without grouping. */
  group?: DueGroup;
  name?: string;
  tasks: MyTask[];
};

/** Filtered, sorted tasks in sections: by due group (fixed order), by project (A–Z) or one. */
export function buildMyTasksView(
  items: readonly MyTask[],
  search: MyTasksSearch,
): MyTasksSection[] {
  const tasks = sortMyTasks(filterMyTasks(items, search), search);
  const group = search.group ?? 'due';
  if (tasks.length === 0) return [];
  if (group === 'none') return [{ id: 'all', tasks }];
  if (group === 'due') {
    return dueGroupSchema.options
      .map((g) => ({ id: g, group: g, tasks: tasks.filter((t) => t.group === g) }))
      .filter((s) => s.tasks.length > 0);
  }
  const byProject = new Map<string, MyTasksSection>();
  for (const task of tasks) {
    const section = byProject.get(task.project.id);
    if (section === undefined) {
      byProject.set(task.project.id, {
        id: task.project.id,
        name: task.project.name,
        tasks: [task],
      });
    } else {
      section.tasks.push(task);
    }
  }
  return [...byProject.values()].sort((a, b) => collator.compare(a.name ?? '', b.name ?? ''));
}

const VIEW_KEY = 'pm-tool.my-tasks-view';

/** Last chosen view in this browser (E-39); the URL wins when it names one. */
export function rememberedView(): MyTasksView | null {
  try {
    const parsed = myTasksViewSchema.safeParse(window.localStorage.getItem(VIEW_KEY));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function rememberView(view: MyTasksView) {
  try {
    window.localStorage.setItem(VIEW_KEY, view);
  } catch {
    // not remembered: the next visit starts with the list
  }
}

export type MyTasksBoardColumnView = { column: MyTasksBoardColumn; tasks: MyTask[] };

/**
 * Board of "Meine Tasks" (E-39): the list filters without the due filter (the columns replace
 * it), every column sorted by due date, priority (high first) and key; empty columns stay.
 */
export function buildMyTasksBoard(
  items: readonly MyTask[],
  search: MyTasksSearch,
  today: string,
): MyTasksBoardColumnView[] {
  const tasks = sortMyTasks(filterMyTasks(items, patchSearch(search, { due: undefined })), {
    sort: 'due',
    dir: 'asc',
  });
  return myTasksBoardColumnSchema.options.map((column) => ({
    column,
    tasks: tasks.filter((t) => myTasksBoardColumn(t.dueDate, today) === column),
  }));
}
